/**
 * The portal's root route component (plan-019 ED-26; design §31.9.1) — owned by plan-019 unit **WAPP-HOST**.
 *
 * plan-019 REG gave the root route of `../router.tsx` this component once, so the help assistant is mounted above every
 * page — the shell's and the way-in pages' — without editing `../shell/shell.tsx`:
 *
 *   <HelpFillProvider>            the fill registry the reviewed forms register with (`./fill/**`, no network)
 *     <HelpEntryContext>          what the person menu's "Help" item does now (`./help-entry.tsx`, OD-24)
 *       <Outlet />                the matched route, exactly as before
 *       <HelpMount />             the widget, on idle, while help is on, never during impersonation (`./help-mount.tsx`)
 *
 * It also drives the host's three signals from the shell's own state, nothing new: the display language (ADR 0051), the
 * scope — a switch, a sign-in or sign-out, a re-read after `409 scope_changed`, keyed by the auth_session's person and
 * scope generation — and the fill suspension while the console's scope generation is stale (`409 scope_changed`, until
 * the auth_session is read again). A modal dialog over the page (the step-up "Confirm it is you", every confirmation)
 * suspends fills at the moment of the fill (FR-HA-21).
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Outlet, useRouter } from "@tanstack/react-router";
import type { HelpWidgetHandle } from "@wetalk/help-widget";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from "react";

import { useGateway } from "../api/provider.js";
import { QUERY_KEY, authContextQuery } from "../api/query.js";
import type { AuthSessionView } from "../api/gateway.js";
import { usePortalTranslation } from "../i18n/portal.js";
import { HelpFillProvider } from "./fill/fill-context.js";
import { createHelpPlaceRegistry, type HelpPlaceRegistry } from "./place/place-registry.js";
import { HelpPlaceProvider } from "./place/use-help-place.js";
import { createHelpFillRegistry, type HelpFillRegistry } from "./fill/fill-registry.js";
import { HELP_ENTRY_NONE, HelpEntryContext, type HelpEntry } from "./help-entry.js";
import { browserAnonymousHelpEnvironment, helpPanelOpenInTab, helpStatusQuery } from "./help-gateway.js";
import {
  HelpMount,
  browserIdleScheduler,
  helpMountModeOf,
  loadHelpWidgetModule,
  type HelpWidgetModule,
  type IdleScheduler,
} from "./help-mount.js";
import { createPortalHelpHost, type PortalHelpHost } from "./host-api.js";
import { holdsInOpenScope } from "./route-access.js";

/** What a suite may replace: the widget module and the idle scheduler. */
export interface HelpRootSeam {
  readonly load: () => Promise<HelpWidgetModule>;
  readonly idle: IdleScheduler;
}

const BROWSER_SEAM: HelpRootSeam = { load: loadHelpWidgetModule, idle: browserIdleScheduler() };

/** The auth_session's identity for the help assistant: who, in which scope generation; `null` signed out. */
function scopeKeyOf(current: AuthSessionView | null | undefined): string | null | undefined {
  if (current === undefined) return undefined;
  return current === null ? null : `${current.person.personId}:${String(current.scopeGeneration)}`;
}

/** The mounted screen's label (`data-screen-label`), for the page context. */
function screenLabelOnPage(): string | null {
  const labelled = globalThis.document.querySelectorAll("[data-screen-label]");
  const last = labelled.item(labelled.length - 1) as Element | null;
  return last === null ? null : last.getAttribute("data-screen-label");
}

/** A modal dialog stands over the page (the console's dialogs are `<dialog>` elements shown modal). */
function modalOpenOnPage(): boolean {
  return globalThis.document.querySelector("dialog[open]") !== null;
}

/** A value the page's callbacks read later; written after each render commits. */
function latestBoxOf<T>(initial: T): { readonly read: () => T; readonly put: (next: T) => void } {
  let value = initial;
  return {
    read: () => value,
    put: (next) => {
      value = next;
    },
  };
}

/**
 * The auth_session as the query CACHE holds it now (plan-019 E2E fix; AC-HA-18): `undefined` while unknown, `null`
 * signed out.
 *
 * A scope switch runs the scope guard's `closePreviousScope()` — `queryClient.clear()` — and then reads the auth_session
 * again into a NEW cache entry. A `useQuery` observer keeps the removed entry and is not re-rendered, so the help root
 * never saw the new scope and never told the widget (no `bye`; the next message continued in the old workspace). This
 * follows the cache itself, as `PortalLanguageBoundary` does (`../i18n/language-control.tsx`), and keeps the last known
 * auth_session across the moment the entry is gone between the clear and the re-read, so the widget is not unmounted
 * and re-mounted by the switch (it is told `scopeChanged()` once the new auth_session arrives).
 */
function useFollowedAuthSession(): AuthSessionView | null | undefined {
  const gateway = useGateway();
  const queries = useQueryClient();
  // Reads the auth_session (and reads it again after a clear): this component re-renders on every cache change below.
  useQuery(authContextQuery(gateway));
  const cached = useSyncExternalStore(
    useCallback((listener: () => void) => queries.getQueryCache().subscribe(listener), [queries]),
    useCallback(() => queries.getQueryData<AuthSessionView | null>(QUERY_KEY.authSession), [queries]),
    () => undefined,
  );
  return useLastKnown(cached);
}

/**
 * `value`, or — while it is `undefined` after having been known (its cache entry cleared and not yet read again) — the
 * last value it had. The switch's clear must not hide and re-show the widget.
 */
function useLastKnown<T>(value: T | undefined): T | undefined {
  const [known, setKnown] = useState(value);
  if (value !== undefined && value !== known) setKnown(value);
  return value === undefined ? known : value;
}

export function HelpRoot(): ReactNode {
  return <HelpRootWith seam={BROWSER_SEAM} />;
}

export function HelpRootWith({ seam }: { readonly seam: HelpRootSeam }): ReactNode {
  const gateway = useGateway();
  const queries = useQueryClient();
  const router = useRouter();
  const { locale } = usePortalTranslation("help");
  const current = useFollowedAuthSession();
  const status = useLastKnown(useQuery(helpStatusQuery(gateway)).data);
  // A panel open in this tab before a reload keeps the widget while help takes no new conversation (its conversation
  // may finish; owner decision 2026-10-15). Read once.
  const [panelWasOpen] = useState(helpPanelOpenInTab);

  // The latest sign-in and language, read by the host's and the registry's functions when the widget calls them.
  const [latest] = useState(() => latestBoxOf<{ readonly current: AuthSessionView | null | undefined; readonly locale: string }>({ current, locale }));
  useLayoutEffect(() => {
    latest.put({ current, locale });
  });

  const registry = useMemo<HelpFillRegistry>(
    () =>
      createHelpFillRegistry({
        holds: (capability) => {
          const known = latest.read().current;
          return known !== undefined && holdsInOpenScope(known, capability, new Date());
        },
        location: () => ({ href: router.state.location.href, idle: router.state.status === "idle" }),
        modalOpen: modalOpenOnPage,
      }),
    [router, latest],
  );

  const places = useMemo<HelpPlaceRegistry>(() => createHelpPlaceRegistry(), []);

  const [handle, setHandle] = useState<HelpWidgetHandle | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const onUnavailable = useCallback(() => {
    setUnavailable(true);
  }, []);

  const consoleVersion = router.options.context.consoleVersion;
  const host = useMemo<PortalHelpHost>(
    () =>
      createPortalHelpHost({
        build: consoleVersion,
        gateway,
        registry,
        places,
        anonymous: browserAnonymousHelpEnvironment(),
        onUnavailable,
        // The widget asks every minute and on every press; the same cache entry the mount and the person menu read.
        available: () => queries.fetchQuery({ ...helpStatusQuery(gateway), staleTime: 0 }).then((answer) => answer.enabled),
        // plan-025: a confirmed card changed (or tried to change) something: every screen on show reads again.
        afterAccountAction: () => {
          void queries.invalidateQueries();
        },
        page: {
          authSession: () => latest.read().current,
          language: () => latest.read().locale,
          location: () => {
            const state = router.state;
            const leaf = state.matches[state.matches.length - 1];
            return { pathname: state.location.pathname, routePath: leaf === undefined ? "" : leaf.fullPath };
          },
          screenLabel: screenLabelOnPage,
          navigateTo: (path) => {
            void router.navigate({ href: path });
          },
          onLocation: (listener) => router.subscribe("onResolved", () => listener()),
          now: () => new Date(),
          document: () => globalThis.document,
        },
      }),
    [consoleVersion, gateway, queries, registry, places, router, onUnavailable, latest],
  );

  // The display language: the widget switches at once and tells the help host (FR-HA-2).
  const languageSeen = useRef(locale);
  useEffect(() => {
    if (languageSeen.current === locale) return;
    languageSeen.current = locale;
    host.languageChanged();
  }, [host, locale]);

  // The scope: a switch, a sign-in or sign-out, a re-read after `409 scope_changed` — a new grant before the next message.
  const scopeKey = scopeKeyOf(current);
  const scopeSeen = useRef(scopeKey);
  useEffect(() => {
    if (scopeKey === undefined) return;
    const previous = scopeSeen.current;
    scopeSeen.current = scopeKey;
    if (previous !== undefined && previous !== scopeKey) host.scopeChanged();
  }, [host, scopeKey]);

  // `409 scope_changed` (or another tab's switch): no fill until the auth_session is read again (FR-HA-21).
  useEffect(() => {
    let release: (() => void) | null = gateway.scopeGeneration.isStale() ? registry.suspend("scope_changed") : null;
    const stop = gateway.scopeGeneration.onChange((event) => {
      if (event.kind === "left" && release === null) release = registry.suspend("scope_changed");
      if (event.kind === "adopted" && release !== null) {
        release();
        release = null;
      }
    });
    return () => {
      stop();
      release?.();
    };
  }, [gateway, registry]);

  // OD-24: "Help" whenever help is on, except for an operator acting as the person (help is refused to them).
  const enabled = status !== undefined && status.enabled;
  // Owner decision 2026-10-15: help that takes no new conversation (the day's budget spent, no GPU pool) hides the
  // launcher and the person menu's Help, but a widget already on the page — or a panel open in this tab before a
  // reload — stays mounted, so an open panel is never taken away mid-conversation (the widget hides its launcher).
  const keep = status !== undefined && (handle !== null || panelWasOpen);
  const mode = helpMountModeOf(enabled || keep ? { enabled: true } : status, current, locale);
  useEffect(() => {
    if (handle !== null && status !== undefined) handle.setAvailable(status.enabled);
  }, [handle, status]);
  const impersonated = current !== undefined && current !== null && current.actor.kind !== "self";
  const entry = useMemo<HelpEntry>(() => {
    if (!enabled || current === undefined || impersonated) return HELP_ENTRY_NONE;
    if (handle !== null && !unavailable) return { kind: "panel", open: handle.open };
    return { kind: "page" };
  }, [enabled, current, impersonated, handle, unavailable]);

  return (
    <HelpFillProvider registry={registry}>
      <HelpPlaceProvider registry={places}>
        <HelpEntryContext value={entry}>
          <Outlet />
          <HelpMount mounted={mode !== "hidden"} host={host} load={seam.load} idle={seam.idle} onHandle={setHandle} />
        </HelpEntryContext>
      </HelpPlaceProvider>
    </HelpFillProvider>
  );
}
